import { shortMemberName, sortMembersWithCurrentFirst } from '@lib/members'
import { currencySymbol } from '@lib/money'
import { getShortName } from '@/utils/userNames'
import CurrencySelect from '@components/CurrencySelect'
import Modal from '@components/Modal'
import { GetUserType, PostPayUpType } from '@orbit/api-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Breadcrumb, BreadcrumbSeparator, BreadcrumbStep } from '../Stepper.styles'
import {
	canGoBack,
	goToStep,
	openingPayUpDraft,
	PayUpDraft,
	PayUpPrefill,
	selectPayer,
	selectRecipient,
	setAmount,
	setCurrencyCode,
	showsBreadcrumb,
	stepBack,
	stepIndicatorLabel,
	toCreatePayload,
	validatePayUpDraft,
} from '../payupForm'
import {
	AmountInput,
	AmountInputWrapper,
	AmountPrefix,
	BackButton,
	Button,
	ButtonGroup,
	FormGroup,
	FormLabel,
	MemberList,
	MemberName,
	PayerRecipientArrow,
	PayerRecipientLabel,
	PayerRecipientName,
	PayerRecipientPerson,
	PayerRecipientRow,
	SoftWarning,
	StepHeading,
	StepIndicator,
	ThirdPartyNotice,
	ValidationError,
} from './AddPayUpModal.styles'
import { OptionRow } from '@components/OptionRow'
import { backgroundOrange } from '@/theme/colors'
import { Avatar } from '@components/Avatar'
import { TestIds } from '@/testIds'

interface AddPayUpModalProps {
	isOpen: boolean
	onClose: () => void
	onSubmit: (payload: PostPayUpType) => void
	members: GetUserType[]
	currentUserId: number
	defaultCurrencyCode: string
	prefill?: PayUpPrefill
	isSubmitting?: boolean
}

const AddPayUpModal: React.FC<AddPayUpModalProps> = ({
	isOpen,
	onClose,
	onSubmit,
	members,
	currentUserId,
	defaultCurrencyCode,
	prefill,
	isSubmitting = false,
}) => {
	const openingDraft = useCallback(
		(): PayUpDraft =>
			openingPayUpDraft({
				memberIds: members.map(m => m.id),
				currentUserId,
				defaultCurrencyCode,
				prefill,
			}),
		[members, currentUserId, defaultCurrencyCode, prefill],
	)

	const [draft, setDraft] = useState<PayUpDraft>(openingDraft)

	useEffect(() => {
		if (isOpen) setDraft(openingDraft())
	}, [isOpen, openingDraft])

	const sortedMembers = useMemo(
		() => sortMembersWithCurrentFirst(members, currentUserId),
		[members, currentUserId],
	)

	const recipientCandidates = useMemo(
		() => sortedMembers.filter(m => m.id !== draft.fromUserId),
		[sortedMembers, draft.fromUserId],
	)

	const fromMember = members.find(m => m.id === draft.fromUserId) ?? null
	const toMember = members.find(m => m.id === draft.toUserId) ?? null

	const symbol = currencySymbol(draft.currencyCode)
	const validation = validatePayUpDraft(draft)
	const canSubmit = validation.canSubmit && !isSubmitting

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (isSubmitting) return
		const payload = toCreatePayload(draft)
		if (!payload) return
		onSubmit(payload)
	}

	const showBackButton = canGoBack(draft)
	const showBreadcrumb = showsBreadcrumb(draft)
	const stepLabel = stepIndicatorLabel(draft)

	const formId = 'add-payup-form'

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Record a Payup"
			testId={TestIds.payUp.recordScreen}
			size="medium"
			backgroundColor={backgroundOrange}
			footer={
				draft.step === 3 ? (
					<ButtonGroup>
						<Button type="button" variant="secondary" onClick={onClose}>
							Cancel
						</Button>
						<Button
							type="submit"
							form={formId}
							variant="primary"
							disabled={!canSubmit}
							data-testid={TestIds.payUp.confirmButton}
						>
							{isSubmitting ? 'Recording...' : 'Confirm Payup'}
						</Button>
					</ButtonGroup>
				) : null
			}
		>
			{showBreadcrumb && (
				<Breadcrumb>
					<BreadcrumbStep
						type="button"
						$current={draft.step === 1}
						disabled={draft.step === 1}
						onClick={() => setDraft(current => goToStep(current, 1))}
					>
						From
					</BreadcrumbStep>
					<BreadcrumbSeparator>›</BreadcrumbSeparator>
					<BreadcrumbStep
						type="button"
						$current={draft.step === 2}
						disabled={draft.step <= 2}
						onClick={() => setDraft(current => goToStep(current, 2))}
					>
						To
					</BreadcrumbStep>
					<BreadcrumbSeparator>›</BreadcrumbSeparator>
					<BreadcrumbStep type="button" $current={draft.step === 3} disabled>
						Details
					</BreadcrumbStep>
				</Breadcrumb>
			)}

			{draft.step === 1 && (
				<>
					<StepIndicator>{stepLabel}</StepIndicator>
					<StepHeading>Who paid?</StepHeading>
					<MemberList>
						{sortedMembers.map(member => (
							<OptionRow
								key={member.id}
								type="button"
								onClick={() => setDraft(current => selectPayer(current, member.id))}
							>
								<Avatar size="lg" user={member} />
								<MemberName>{shortMemberName(member, currentUserId)}</MemberName>
							</OptionRow>
						))}
					</MemberList>
				</>
			)}

			{draft.step === 2 && (
				<>
					{showBackButton && (
						<BackButton type="button" onClick={() => setDraft(stepBack)}>
							← Back
						</BackButton>
					)}
					<StepIndicator>{stepLabel}</StepIndicator>
					<StepHeading>Who received the payment?</StepHeading>
					<MemberList>
						{recipientCandidates.map(member => (
							<OptionRow
								key={member.id}
								type="button"
								onClick={() => setDraft(current => selectRecipient(current, member.id))}
							>
								<Avatar size="lg" user={member} />
								<MemberName>{shortMemberName(member, currentUserId)}</MemberName>
							</OptionRow>
						))}
					</MemberList>
				</>
			)}

			{draft.step === 3 && fromMember && toMember && (
				<form id={formId} onSubmit={handleSubmit}>
					{showBackButton && (
						<BackButton type="button" onClick={() => setDraft(stepBack)}>
							← Back
						</BackButton>
					)}
					{stepLabel && <StepIndicator>{stepLabel}</StepIndicator>}

					<PayerRecipientRow>
						<PayerRecipientPerson>
							<PayerRecipientLabel>From</PayerRecipientLabel>
							<Avatar size="lg" user={fromMember} />
							<PayerRecipientName>
								{shortMemberName(fromMember, currentUserId)}
							</PayerRecipientName>
						</PayerRecipientPerson>
						<PayerRecipientArrow>→</PayerRecipientArrow>
						<PayerRecipientPerson>
							<PayerRecipientLabel>To</PayerRecipientLabel>
							<Avatar size="lg" user={toMember} />
							<PayerRecipientName>
								{shortMemberName(toMember, currentUserId)}
							</PayerRecipientName>
						</PayerRecipientPerson>
					</PayerRecipientRow>

					<FormGroup>
						<FormLabel htmlFor="payup-amount">Amount</FormLabel>
						<AmountInputWrapper>
							<AmountPrefix>{symbol}</AmountPrefix>
							<AmountInput
								id="payup-amount"
								type="number"
								value={draft.amount}
								onChange={e => setDraft(current => setAmount(current, e.target.value))}
								placeholder="0.00"
								min="0.01"
								step="0.01"
								autoFocus
							/>
						</AmountInputWrapper>
						{validation.showsHighAmountWarning && (
							<SoftWarning>That seems high — please confirm.</SoftWarning>
						)}
					</FormGroup>

					<FormGroup>
						<CurrencySelect
							value={draft.currencyCode}
							onChange={code => setDraft(current => setCurrencyCode(current, code))}
							label="Currency"
							htmlFor="payup-currency"
						/>
					</FormGroup>

					{validation.error && draft.amount !== '' && (
						<ValidationError>{validation.error}</ValidationError>
					)}

					{draft.fromUserId !== currentUserId && draft.toUserId !== currentUserId && (
						<ThirdPartyNotice>
							*You&apos;re recording a payup between {getShortName(fromMember)} and{' '}
							{getShortName(toMember)} on their behalf.
						</ThirdPartyNotice>
					)}
				</form>
			)}
		</Modal>
	)
}

export default AddPayUpModal
